import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';

// Screen-reader-only style (charts render to <svg>/<canvas> which AT can't
// read). Mirrors @mui/utils' visuallyHidden. NOTE: width/height MUST be string
// px — MUI's sx sizing transform turns the number 1 into "100%", which would
// make this a full-size, nowrap, absolutely-positioned table that overflows the
// page horizontally. Pixel strings bypass that transform.
const srOnly: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * Wraps a chart (e.g. a recharts <ResponsiveContainer>) so assistive tech sees
 * one labeled image instead of an empty SVG. Pass a concise `label` (what the
 * chart is) and optionally a `description` (the headline takeaway / range).
 * For charts whose individual data points matter, also render a
 * <VisuallyHiddenTable> alongside so the numbers themselves are reachable.
 */
export function AccessibleChart({
  label,
  description,
  children,
  sx,
}: {
  label: string;
  description?: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      role="img"
      aria-label={description ? `${label}. ${description}` : label}
      // minWidth:0 + overflow:hidden stop a recharts SVG (which has an intrinsic
      // size) from forcing its flex/Grid parent wider or taller than the
      // viewport — the cause of runaway horizontal/vertical scroll. Height comes
      // from the caller's sx; never default to height:100% here, which would feed
      // a ResponsiveContainer resize loop in an auto-height parent.
      sx={{ position: 'relative', width: '100%', minWidth: 0, overflow: 'hidden', ...sx }}
    >
      {children}
    </Box>
  );
}

/**
 * Screen-reader-only data table. Renders nothing visible but exposes the
 * underlying series/ranking as a real <table>, so AT users get the numbers a
 * canvas/svg chart hides. `caption` names the dataset.
 */
export function VisuallyHiddenTable({
  caption,
  columns,
  rows,
}: {
  caption: string;
  columns: string[];
  rows: (string | number)[][];
}) {
  if (rows.length === 0) return null;
  // srOnly MUST wrap a <div>, NOT sit on the <table>: a <table> treats
  // height:1px as a minimum and is NOT clipped by overflow:hidden, so a
  // many-row table keeps its full height (6000px+) and — being position:
  // absolute — extends the document's scroll region into a huge empty area
  // ("scroll forever"). A <div> wrapper respects overflow:hidden and clips
  // the table down to 1px while screen readers still read it.
  return (
    <Box sx={srOnly}>
      <table>
        <caption>{caption}</caption>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} scope="col">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}
